/**
 * Le vocabulaire d'animation du produit.
 *
 * Huit gestes pour l'application, quatre de plus pour la page vitrine, et
 * rien d'autre. Un écran choisit lequel il emploie ; il n'écrit ni tween, ni
 * ease, ni durée. C'est ce qui permet à cinq écrans développés en parallèle
 * de bouger de la même façon.
 *
 * Chaque fonction rend une `ref` à poser sur un élément. Le nettoyage au
 * démontage est assuré par `useGSAP` : aucun appelant n'a de `kill()` à écrire.
 *
 * Mouvements courts (0,3 à 0,6 s) et fonctionnels. Sur un dashboard consulté
 * dix fois par jour, une transition trop longue n'est pas une élégance, c'est
 * une nuisance.
 */
import { useCallback, useRef } from "react";
import type { RefObject } from "react";
import { gsap, matchMotion, prefersReducedMotion, useGSAP } from "./gsap";
import type { Int64 } from "../api/types";
import { formatTokens } from "../format";

/** Attribut que les éléments animables portent par défaut. */
const SELECTEUR_CARTE = "[data-anim='card']";
const SELECTEUR_LIGNE = "[data-anim='row']";
const SELECTEUR_BARRE = "[data-anim='bar']";

export type CardEntranceOptions = {
  /** Quoi animer dans le conteneur. Défaut : `[data-anim='card']`. */
  selector?: string;
  /** Décalage entre deux cartes, en secondes. Défaut : `0,07`. */
  stagger?: number;
  /** Distance de montée, en pixels. Défaut : `16`. */
  y?: number;
  /** Retard avant le premier élément. Défaut : `0`. */
  delay?: number;
  /**
   * Rejoue l'entrée quand ces valeurs changent. Typiquement les données :
   * `[packs]`, pour que les cartes arrivées après le chargement s'animent
   * elles aussi, au lieu d'apparaître d'un coup.
   */
  dependencies?: unknown[];
};

/**
 * **Entrée de cartes en cascade.**
 *
 * Usage : l'arrivée d'un groupe de tuiles ou de cartes — les quatre seaux du
 * dashboard, la grille des packs, les expertises du catalogue. Le décalage donne
 * à l'œil le temps de compter les éléments avant de les lire.
 *
 * ```tsx
 * const grille = useCardEntrance({ dependencies: [packs] });
 * <div ref={grille}>
 *   {packs.map((p) => <Card key={p.id} data-anim="card">…</Card>)}
 * </div>
 * ```
 */
export function useCardEntrance<T extends HTMLElement = HTMLDivElement>(
  options: CardEntranceOptions = {},
): RefObject<T | null> {
  const {
    selector = SELECTEUR_CARTE,
    stagger = 0.07,
    y = 16,
    delay = 0,
    dependencies = [],
  } = options;
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      // Sans ce garde, `toArray` retomberait sur `document` et animerait toutes
      // les cartes de l'application, y compris celles d'un autre écran.
      if (conteneur.current === null) {
        return;
      }
      const cibles = gsap.utils.toArray<HTMLElement>(selector, conteneur.current);
      if (cibles.length === 0) {
        return;
      }
      return matchMotion(({ reduced, duration }) =>
        // `from` et non `to` : si l'animation ne se jouait jamais (préférence
        // exotique, plugin bloqué), les cartes restent visibles. Pré-cacher en
        // CSS ferait l'inverse — un écran vide au moindre incident.
        void gsap.from(cibles, {
          autoAlpha: 0,
          y,
          duration: duration(0.5),
          delay: duration(delay),
          stagger: reduced ? 0 : stagger,
          // Sans ça, le `transform` inline laissé par GSAP écrase le
          // `translateY` de survol défini en CSS sur les cartes.
          clearProps: "opacity,visibility,transform",
        }),
      );
    },
    { scope: conteneur, dependencies: [selector, stagger, y, delay, ...dependencies] },
  );

  return conteneur;
}

export type CountUpOptions = {
  /** Mise en forme de chaque étape. Défaut : `formatTokens`. */
  format?: (value: Int64) => string;
  /** Durée de la montée, en secondes. Défaut : `0,6`. */
  duration?: number;
};

/**
 * **Compteur qui monte jusqu'à sa valeur.**
 *
 * Usage : un solde qui change — le disponible du dashboard après un achat, le
 * consommé d'une mission après une imputation. Le mouvement du chiffre dit
 * « ceci vient de bouger » sans le moindre texte.
 *
 * Au premier rendu, la montée part de zéro ; ensuite, de la valeur précédente.
 *
 * Les étapes intermédiaires passent par un `number` — c'est une valeur
 * d'affichage éphémère, pas une écriture. **La dernière image, elle, écrit la
 * chaîne exacte reçue de l'API**, donc aucun solde affiché n'est arrondi. Au
 * delà de 2^53, la montée est sautée et la valeur exacte s'affiche directement.
 *
 * ```tsx
 * const solde = useCountUp(balance.available);
 * <span ref={solde} className="lde-tabular" />
 * ```
 * L'élément n'a pas d'enfant : c'est le compteur qui écrit son texte.
 */
export function useCountUp<T extends HTMLElement = HTMLSpanElement>(
  value: Int64,
  options: CountUpOptions = {},
): RefObject<T | null> {
  const { format = formatTokens, duration = 0.6 } = options;
  const element = useRef<T | null>(null);
  const precedent = useRef<Int64>("0");

  useGSAP(
    () => {
      const cible = element.current;
      if (!cible) {
        return;
      }
      const depart = precedent.current;
      precedent.current = value;
      const texteFinal = format(value);

      const de = Number(depart);
      const vers = Number(value);
      const horsPortee =
        !Number.isSafeInteger(de) || !Number.isSafeInteger(vers);

      return matchMotion(({ reduced, duration: d }) => {
        if (reduced || horsPortee || de === vers) {
          cible.textContent = texteFinal;
          return;
        }
        const proxy = { v: de };
        const tween = gsap.to(proxy, {
          v: vers,
          duration: d(duration),
          ease: "power2.out",
          onUpdate: () => {
            cible.textContent = format(Math.round(proxy.v).toString());
          },
          onComplete: () => {
            cible.textContent = texteFinal;
          },
        });
        return () => {
          tween.kill();
          cible.textContent = texteFinal;
        };
      });
    },
    { dependencies: [value] },
  );

  return element;
}

export type GaugeFillOptions = {
  /** Durée du remplissage, en secondes. Défaut : `0,6`. */
  duration?: number;
};

/**
 * **Remplissage d'une jauge de seau.**
 *
 * Usage : la part d'un seau dans le total, l'avancement d'une mission. Le
 * remplissage se lit comme une quantité ; une barre qui apparaît déjà pleine ne
 * se lit pas.
 *
 * La ref se pose sur l'**élément de remplissage**, pas sur la piste. Cet
 * élément doit occuper toute la largeur de la piste : c'est `scaleX` qui le
 * réduit, jamais `width` — animer une largeur force un recalcul de mise en page
 * à chaque image, `scaleX` reste sur le compositeur.
 *
 * ```tsx
 * const jauge = useGaugeFill(ratio(balance.available, balance.total));
 * <div className={styles.piste}><div ref={jauge} className={styles.remplissage} /></div>
 * ```
 *
 * @param value part à atteindre, entre 0 et 1 (voir `ratio()` de `format/`).
 */
export function useGaugeFill<T extends HTMLElement = HTMLDivElement>(
  value: number,
  options: GaugeFillOptions = {},
): RefObject<T | null> {
  const { duration = 0.6 } = options;
  const element = useRef<T | null>(null);
  const dejaRempli = useRef(false);

  useGSAP(
    () => {
      const cible = element.current;
      if (!cible) {
        return;
      }
      const cible0a1 = Math.min(1, Math.max(0, value));
      const premier = !dejaRempli.current;
      dejaRempli.current = true;

      return matchMotion(({ duration: d }) => {
        const commun = {
          scaleX: cible0a1,
          transformOrigin: "left center",
          duration: d(duration),
        };
        // Au premier rendu la jauge se remplit depuis zéro ; ensuite elle se
        // déplace depuis sa valeur courante — sinon chaque mise à jour du solde
        // ferait repartir toutes les jauges de la gauche, ce qui donne
        // l'impression fausse que tout a changé.
        void (premier
          ? gsap.fromTo(cible, { scaleX: 0 }, commun)
          : gsap.to(cible, commun));
      });
    },
    { dependencies: [value] },
  );

  return element;
}

export type BarsGrowOptions = {
  /** Quoi faire pousser dans le conteneur. Défaut : `[data-anim='bar']`. */
  selector?: string;
  /** Décalage entre deux barres, en secondes. Défaut : `0,04`. */
  stagger?: number;
  /** Rejoue la pousse quand ces valeurs changent : les données, le rythme choisi. */
  dependencies?: unknown[];
};

/**
 * **Des barres qui poussent depuis leur base.**
 *
 * Usage : un histogramme qui arrive, ou dont la série change. Une barre qui
 * pousse se lit comme une quantité ; une barre qui apparaît déjà pleine ne
 * se lit pas — c'est la même raison que pour `useGaugeFill`, à la verticale.
 *
 * La ref se pose sur le `<svg>` (ou le conteneur) ; chaque barre porte
 * `data-anim="bar"`. Une courbe tracée à côté peut porter `data-anim="line"` :
 * elle se dessine de gauche à droite pendant que les barres poussent, et
 * l'aire sous elle (`data-anim="area"`) apparaît en fondu derrière. La pousse est un `scaleY` depuis l'origine basse de
 * chaque barre : GSAP résout l'origine sur la boîte de l'élément SVG, donc
 * chaque barre part de sa propre base, quelle que soit sa hauteur.
 *
 * ```tsx
 * const svg = useBarsGrow({ dependencies: [serie] });
 * <svg ref={svg}>{barres.map((b) => <path key={b.cle} data-anim="bar" d={…} />)}</svg>
 * ```
 */
export function useBarsGrow<T extends Element = SVGSVGElement>(
  options: BarsGrowOptions = {},
): RefObject<T | null> {
  const { selector = SELECTEUR_BARRE, stagger = 0.04, dependencies = [] } = options;
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      if (conteneur.current === null) {
        return;
      }
      const cibles = gsap.utils.toArray<Element>(selector, conteneur.current);
      const lignes = gsap.utils.toArray<SVGPathElement>("[data-anim='line']", conteneur.current);
      const aires = gsap.utils.toArray<Element>("[data-anim='area']", conteneur.current);
      if (cibles.length === 0 && lignes.length === 0) {
        return;
      }
      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          return;
        }
        // `from` : si l'animation ne se jouait pas, les barres restent à leur
        // hauteur finale — un histogramme vide au moindre incident serait pire
        // qu'un histogramme immobile.
        const tweens = [
          gsap.from(cibles, {
            scaleY: 0,
            transformOrigin: "50% 100%",
            duration: duration(0.5),
            stagger,
            clearProps: "transform",
          }),
          gsap.from(aires, { autoAlpha: 0, duration: duration(0.8), delay: duration(0.3), clearProps: "opacity,visibility" }),
        ];
        // Le tracé : un pointillé aussi long que la courbe, décalé de toute sa
        // longueur, ramené à zéro. Les propriétés sont retirées à la fin, la
        // courbe retrouve son trait d'origine.
        for (const ligne of lignes) {
          const longueur = ligne.getTotalLength();
          tweens.push(
            gsap.fromTo(
              ligne,
              { strokeDasharray: longueur, strokeDashoffset: longueur },
              { strokeDashoffset: 0, duration: duration(1.1), ease: "power2.inOut", clearProps: "strokeDasharray,strokeDashoffset" },
            ),
          );
        }
        return () => {
          tweens.forEach((t) => t.revert());
        };
      });
    },
    { scope: conteneur, dependencies: [selector, stagger, ...dependencies] },
  );

  return conteneur;
}

export type RevealRowsOptions = {
  /** Quoi révéler dans le conteneur. Défaut : `[data-anim='row']`. */
  selector?: string;
  /** Distance de montée, en pixels. Défaut : `12`. */
  y?: number;
  /** Rejoue la révélation quand ces valeurs changent (une page de plus). */
  dependencies?: unknown[];
};

/**
 * **Arrivée des lignes d'une liste.**
 *
 * Usage : les listes longues — l'historique du grand livre, le tableau des
 * missions. Les lignes visibles à l'affichage montent l'une après l'autre ;
 * celles sous la ligne de flottaison sont là d'emblée, sans attendre qu'on
 * fasse défiler.
 *
 * La version précédente cachait chaque ligne jusqu'à son entrée dans la
 * fenêtre (`ScrollTrigger.batch`) : le bas d'un tableau paraissait vide, une
 * capture ou une impression sortait blanche, et un défilement rapide montrait
 * des trous. Une liste est un contenu, pas un décor : elle doit être complète
 * au repos.
 *
 * ```tsx
 * const corps = useRevealRows<HTMLTableSectionElement>({ dependencies: [entries] });
 * <tbody ref={corps}>{entries.map((e) => <tr key={e.id} data-anim="row">…</tr>)}</tbody>
 * ```
 */
export function useRevealRows<T extends HTMLElement = HTMLElement>(
  options: RevealRowsOptions = {},
): RefObject<T | null> {
  const { selector = SELECTEUR_LIGNE, y = 12, dependencies = [] } = options;
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      // Même garde que pour l'entrée de cartes : une ref non attachée ferait
      // porter la révélation sur toutes les lignes du document.
      if (conteneur.current === null) {
        return;
      }
      const hauteur = window.innerHeight;
      const visibles = gsap.utils
        .toArray<HTMLElement>(selector, conteneur.current)
        .filter((ligne) => ligne.getBoundingClientRect().top < hauteur)
        // Au-delà d'une douzaine, l'enchaînement durerait plus d'une demi-seconde.
        .slice(0, 12);
      if (visibles.length === 0) {
        return;
      }

      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          return;
        }
        // `from` : l'état de repos est l'état visible, l'animation ne fait que
        // partir d'un léger décalage. Interrompue, elle laisse la ligne lisible.
        const tween = gsap.from(visibles, {
          autoAlpha: 0,
          y,
          duration: duration(0.4),
          stagger: 0.04,
          clearProps: "opacity,visibility,transform",
        });
        return () => {
          tween.revert();
        };
      });
    },
    { scope: conteneur, dependencies: [selector, y, ...dependencies] },
  );

  return conteneur;
}

/**
 * **Arrivée d'un écran.**
 *
 * Usage : la coquille, une seule fois, sur le conteneur de la page. Chaque
 * changement d'écran fait monter ses blocs de quelques pixels, en fondu : on
 * voit qu'on a changé de page sans attendre. `cle` dit ce qu'est « un écran » ;
 * la coquille y passe le premier segment du chemin, pour qu'un changement
 * d'onglet dans la même page ne fasse pas sauter son en-tête.
 *
 * Court (0,25 s) et depuis un état visible : interrompu par un clic, le
 * contenu reste lisible.
 *
 * ```tsx
 * const page = usePageEnter<HTMLDivElement>(segment);
 * <div ref={page}><Outlet /></div>
 * ```
 */
export function usePageEnter<T extends HTMLElement = HTMLDivElement>(cle: string): RefObject<T | null> {
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      if (conteneur.current === null) {
        return;
      }
      const blocs = Array.from(conteneur.current.children);
      if (blocs.length === 0) {
        return;
      }
      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          return;
        }
        const tween = gsap.from(blocs, {
          autoAlpha: 0,
          y: 8,
          duration: duration(0.25),
          stagger: 0.04,
          clearProps: "opacity,visibility,transform",
        });
        return () => {
          tween.revert();
        };
      });
    },
    { scope: conteneur, dependencies: [cle] },
  );

  return conteneur;
}

export type HighlightOptions = {
  /** Briller aussi au premier affichage : un reçu qui vient d'apparaître. */
  initial?: boolean;
};

/** `rgb(r, g, b)` ou `rgba(r, g, b, a)` → la même couleur à l'opacité voulue. */
function avecAlpha(couleur: string, alpha: number): string {
  const canaux = couleur.match(/[\d.]+/g);
  if (canaux === null || canaux.length < 3) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  return `rgba(${canaux[0]}, ${canaux[1]}, ${canaux[2]}, ${alpha})`;
}

/**
 * **Halo d'un chiffre qui vient de changer.**
 *
 * Usage : une tuile dont la valeur bouge sous les yeux — le consommé d'un
 * projet après une imputation, les tokens d'un reçu d'achat. Un anneau de la
 * couleur du seau s'élargit et s'efface une fois : l'œil va au chiffre qui a
 * bougé, sans message de plus.
 *
 * Rien au premier rendu (sauf `initial`) : une page qui s'ouvre n'a rien
 * « changé ». La couleur est lue sur l'élément (`--seau`, à défaut l'encre),
 * résolue par le navigateur : GSAP ne sait pas interpoler un `color-mix()`.
 *
 * ```tsx
 * const tuile = useHighlight<HTMLDivElement>(mission.consumed_tokens);
 * <div ref={tuile}>…</div>
 * ```
 */
export function useHighlight<T extends HTMLElement = HTMLDivElement>(
  value: unknown,
  options: HighlightOptions = {},
): RefObject<T | null> {
  const { initial = false } = options;
  const element = useRef<T | null>(null);
  const premier = useRef(true);

  useGSAP(
    () => {
      const cible = element.current;
      const ignorer = premier.current && !initial;
      premier.current = false;
      if (cible === null || ignorer || value === undefined) {
        return;
      }
      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          return;
        }
        // Une propriété standard résout `var()` et `color-mix()` en `rgb()`,
        // ce qu'une propriété personnalisée lue telle quelle ne fait pas.
        cible.style.setProperty("outline-color", "var(--seau, currentColor)");
        const couleur = getComputedStyle(cible).outlineColor;
        cible.style.removeProperty("outline-color");
        const tween = gsap.fromTo(
          cible,
          { boxShadow: `0 0 0 0px ${avecAlpha(couleur, 0.4)}` },
          {
            boxShadow: `0 0 0 10px ${avecAlpha(couleur, 0)}`,
            duration: duration(0.8),
            ease: "power2.out",
            clearProps: "boxShadow",
          },
        );
        return () => {
          tween.revert();
        };
      });
    },
    { dependencies: [value, initial] },
  );

  return element;
}

/**
 * **Secousse brève de refus.**
 *
 * Usage : l'API refuse — solde insuffisant, dépassement d'enveloppe, champ
 * invalide. La secousse dit « non » avant même que le texte soit lu, et attire
 * l'œil sur l'élément fautif plutôt que sur un message en haut de page.
 *
 * À déclencher sur un refus, jamais sur une panne réseau : rien ne serait
 * reproché à l'utilisateur.
 *
 * ```tsx
 * const { ref, shake } = useShake<HTMLFormElement>();
 * …
 * const recu = await achat.run(…);
 * if (!recu) shake();
 * <form ref={ref}>…</form>
 * ```
 */
export function useShake<T extends HTMLElement = HTMLElement>(): {
  ref: RefObject<T | null>;
  shake: () => void;
} {
  const element = useRef<T | null>(null);
  const { contextSafe } = useGSAP({ scope: element });

  const shake = useCallback(() => {
    const cible = element.current;
    if (!cible) {
      return;
    }
    // Déclenchement impératif : pas de `matchMedia` ici, mais le même refus de
    // secouer quelqu'un qui a demandé moins de mouvement.
    if (prefersReducedMotion()) {
      return;
    }
    // `contextSafe` rattache le tween au contexte du composant : sans lui, un
    // tween lancé depuis un gestionnaire d'événement échappe au nettoyage de
    // `useGSAP` et survit au démontage.
    contextSafe(() => {
      gsap.fromTo(
        cible,
        { x: -8 },
        { x: 0, duration: 0.45, ease: "elastic.out(1, 0.35)", clearProps: "x" },
      );
    })();
  }, [contextSafe]);

  return { ref: element, shake };
}

// ————————————————————————————— Page vitrine —————————————————————————————
//
// Les quatre gestes ci-dessous ne servent qu'à la page d'accueil publique :
// c'est le seul écran qu'on lit une fois, pas dix fois par jour. Ils admettent
// des séquences et des durées jusqu'à 0,8 s, mais restent des fonctions du
// vocabulaire — un écran ne les recompose pas à la main. Voir REGLES.md,
// section « La page vitrine ».

const SELECTEUR_HERO = "[data-anim='hero']";
const SELECTEUR_HERO_VISUEL = "[data-anim='hero-visual']";
const SELECTEUR_REVEAL = "[data-anim='reveal']";

export type HeroTimelineOptions = {
  /** Décalage entre deux lignes de texte, en secondes. Défaut : `0,09`. */
  stagger?: number;
  /** Retard avant la première ligne. Défaut : `0,1`. */
  delay?: number;
};

/**
 * **Entrée orchestrée du héros.**
 *
 * Usage : l'ouverture de la vitrine, une seule fois. Les textes marqués
 * `data-anim="hero"` montent en cascade, puis le visuel `data-anim="hero-visual"`
 * arrive en chevauchant la fin des textes. Une timeline et non des `delay` :
 * c'est la position relative qui tient la chorégraphie quand une ligne s'ajoute.
 *
 * Un titre se découpe en lignes dans le JSX (`<span data-anim="hero">`) : un
 * seul `from` par élément, sinon le second lirait l'état déjà modifié par le
 * premier (`immediateRender`).
 *
 * En mouvement réduit : rien ne se joue, tout est déjà à sa place — aucun
 * pré-masquage CSS, donc aucun écran vide si l'animation ne se joue pas.
 */
export function useHeroTimeline<T extends HTMLElement = HTMLElement>(
  options: HeroTimelineOptions = {},
): RefObject<T | null> {
  const { stagger = 0.09, delay = 0.1 } = options;
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      if (conteneur.current === null) {
        return;
      }
      const textes = gsap.utils.toArray<HTMLElement>(SELECTEUR_HERO, conteneur.current);
      const visuel = gsap.utils.toArray<HTMLElement>(SELECTEUR_HERO_VISUEL, conteneur.current);
      if (textes.length === 0 && visuel.length === 0) {
        return;
      }
      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          return;
        }
        const tl = gsap.timeline({ defaults: { duration: duration(0.7) }, delay: duration(delay) });
        tl.addLabel("textes");
        if (textes.length > 0) {
          tl.from(textes, { autoAlpha: 0, y: 28, stagger, clearProps: "opacity,visibility,transform" }, "textes");
        }
        if (visuel.length > 0) {
          tl.addLabel("visuel", textes.length > 0 ? "-=0.45" : "textes");
          tl.from(
            visuel,
            { autoAlpha: 0, y: 24, scale: 0.97, transformOrigin: "50% 60%", clearProps: "opacity,visibility,transform" },
            "visuel",
          );
        }
        return () => {
          tl.kill();
        };
      });
    },
    { scope: conteneur, dependencies: [stagger, delay] },
  );

  return conteneur;
}

export type SectionRevealOptions = {
  /** Quoi révéler dans la section. Défaut : `[data-anim='reveal']`. */
  selector?: string;
  /** Distance de montée, en pixels. Défaut : `24`. */
  y?: number;
  /** Décalage entre deux éléments, en secondes. Défaut : `0,08`. */
  stagger?: number;
  /** Position de déclenchement ScrollTrigger. Défaut : `"top 80%"`. */
  start?: string;
  /** Rejoue quand ces valeurs changent. */
  dependencies?: unknown[];
};

/**
 * **Révélation d'une section à son entrée dans la fenêtre.**
 *
 * Usage : les sections de la vitrine. Une seule fois (`once`) : une section
 * qui se recache quand on remonte fait douter de ce qu'on a lu. Un
 * ScrollTrigger par section : trois à six cartes se contentent d'un `stagger`.
 *
 * En mouvement réduit : les éléments sont posés à l'état final, sans
 * déclencheur.
 */
export function useSectionReveal<T extends HTMLElement = HTMLElement>(
  options: SectionRevealOptions = {},
): RefObject<T | null> {
  const { selector = SELECTEUR_REVEAL, y = 24, stagger = 0.08, start = "top 80%", dependencies = [] } = options;
  const conteneur = useRef<T | null>(null);

  useGSAP(
    () => {
      const section = conteneur.current;
      if (section === null) {
        return;
      }
      const cibles = gsap.utils.toArray<HTMLElement>(selector, section);
      if (cibles.length === 0) {
        return;
      }
      return matchMotion(({ reduced, duration }) => {
        if (reduced) {
          gsap.set(cibles, { autoAlpha: 1, y: 0 });
          return;
        }
        gsap.set(cibles, { autoAlpha: 0, y });
        const tween = gsap.to(cibles, {
          autoAlpha: 1,
          y: 0,
          stagger,
          duration: duration(0.6),
          clearProps: "opacity,visibility,transform",
          scrollTrigger: { trigger: section, start, once: true },
        });
        return () => {
          tween.scrollTrigger?.kill();
          tween.kill();
          gsap.set(cibles, { clearProps: "opacity,visibility,transform" });
        };
      });
    },
    { scope: conteneur, dependencies: [selector, y, stagger, start, ...dependencies] },
  );

  return conteneur;
}

export type ParallaxOptions = {
  /** Dérive verticale totale, en pixels. Défaut : `48`. */
  y?: number;
  /** Retard de rattrapage du défilement, en secondes. Défaut : `0,6`. */
  scrub?: number;
};

/**
 * **Dérive légère d'un décor au défilement.**
 *
 * Usage : le décor du héros, qui glisse un peu moins vite que la page pendant
 * que la section sort de l'écran. Transforms seuls, `ease: "none"` : la
 * position suit le défilement, pas une courbe. C'est le seul `scrub` du
 * produit, et il reste dans le héros.
 *
 * En mouvement réduit : rien.
 */
export function useParallax<T extends HTMLElement = HTMLElement>(
  options: ParallaxOptions = {},
): RefObject<T | null> {
  const { y = 48, scrub = 0.6 } = options;
  const element = useRef<T | null>(null);

  useGSAP(
    () => {
      const decor = element.current;
      if (decor === null) {
        return;
      }
      return matchMotion(({ reduced }) => {
        if (reduced) {
          return;
        }
        const tween = gsap.to(decor, {
          y,
          ease: "none",
          scrollTrigger: {
            trigger: decor.closest("section") ?? decor,
            start: "top top",
            end: "bottom top",
            scrub,
            invalidateOnRefresh: true,
          },
        });
        return () => {
          tween.scrollTrigger?.kill();
          tween.kill();
          gsap.set(decor, { clearProps: "transform" });
        };
      });
    },
    { scope: element, dependencies: [y, scrub] },
  );

  return element;
}

/** Les quatre seaux, dans l'ordre des colonnes du visuel. */
export type LedgerBucket = "available" | "reserved" | "consumed" | "expired";

/** Une étape du scénario de conservation. `from` nul : l'argent entre. */
export type LedgerStep = {
  step: "achat" | "reserve" | "consomme" | "libere" | "expire";
  from: LedgerBucket | null;
  to: LedgerBucket;
  amount: number;
};

/** Le total qui entre au départ ; les hauteurs de seau sont des parts de lui. */
export const LEDGER_TOTAL = 1000;

/**
 * Le scénario joué par `useLedgerFlow`, exporté pour que le visuel écrive les
 * mêmes montants sur ses pilules : 1 000 entrent, 400 sont réservés, 250
 * consommés, 150 libérés à la clôture, 50 périmés. À la fin :
 * 700 + 0 + 250 + 50 = 1 000.
 */
export const LEDGER_SCENARIO: readonly LedgerStep[] = [
  { step: "achat", from: null, to: "available", amount: 1000 },
  { step: "reserve", from: "available", to: "reserved", amount: 400 },
  { step: "consomme", from: "reserved", to: "consumed", amount: 250 },
  { step: "libere", from: "reserved", to: "available", amount: 150 },
  { step: "expire", from: "available", to: "expired", amount: 50 },
];

export type LedgerFlowOptions = {
  /** Retard avant la première étape, en secondes. Défaut : `0,6`. */
  delay?: number;
};

const formatEntier = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

/** Les soldes des quatre seaux après chaque étape. */
function soldesParEtape(): number[][] {
  const soldes = { available: 0, reserved: 0, consumed: 0, expired: 0 };
  return LEDGER_SCENARIO.map((s) => {
    if (s.from !== null) {
      soldes[s.from] -= s.amount;
    }
    soldes[s.to] += s.amount;
    return [soldes.available, soldes.reserved, soldes.consumed, soldes.expired];
  });
}

/**
 * **La conservation des quatre seaux, jouée une fois.**
 *
 * Usage : le visuel du héros. Chaque étape du scénario fait glisser une pilule
 * d'une colonne à l'autre ; à son arrivée, le seau de départ baisse et celui
 * d'arrivée monte **au même instant**, les compteurs suivent, et l'équation
 * se réécrit. Ce qu'on voit : la somme ne bouge jamais. L'animation porte
 * une information — la règle du grand livre — et ne boucle pas : elle se
 * rejoue sur le bouton `replay`.
 *
 * Contrat DOM, dans le `<svg>` porteur de la `ref` :
 * - un remplissage par seau : `data-anim="ledger-fill" data-bucket="…"`, de
 *   hauteur pleine, réduit en `scaleY` depuis sa base ;
 * - un compteur par seau : `data-anim="ledger-count" data-bucket="…"`, vide ;
 * - la barre du total entré : `data-anim="ledger-total"`, pleine, `scaleX` ;
 * - une pilule par étape : `data-anim="ledger-flow" data-step="…"` avec
 *   `data-x0 data-y0 data-x1 data-y1` (unités SVG) ;
 * - une légende par étape : `data-anim="ledger-caption" data-step="…"` ;
 * - l'équation : `data-anim="ledger-equation"`, vide.
 *
 * Tout en `set` + `to`/`fromTo`, jamais `from` : plusieurs étapes touchent le
 * même seau. En mouvement réduit : l'état final est posé, la dernière légende
 * visible, `replay` inerte.
 */
export function useLedgerFlow<T extends Element = SVGSVGElement>(
  options: LedgerFlowOptions = {},
): { ref: RefObject<T | null>; replay: () => void } {
  const { delay = 0.6 } = options;
  const conteneur = useRef<T | null>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);

  const { contextSafe } = useGSAP(
    () => {
      const svg = conteneur.current;
      if (svg === null) {
        return;
      }
      const remplissage = (b: LedgerBucket) => svg.querySelector(`[data-anim='ledger-fill'][data-bucket='${b}']`);
      const compteur = (b: LedgerBucket) => svg.querySelector(`[data-anim='ledger-count'][data-bucket='${b}']`);
      const pilule = (s: string) => svg.querySelector<SVGGraphicsElement>(`[data-anim='ledger-flow'][data-step='${s}']`);
      const legende = (s: string) => svg.querySelector(`[data-anim='ledger-caption'][data-step='${s}']`);
      const total = svg.querySelector("[data-anim='ledger-total']");
      const equation = svg.querySelector("[data-anim='ledger-equation']");
      const seaux: LedgerBucket[] = ["available", "reserved", "consumed", "expired"];
      const soldes = soldesParEtape();
      const final = soldes[soldes.length - 1];

      const ecrire = (valeurs: number[]) => {
        seaux.forEach((b, i) => {
          const c = compteur(b);
          if (c) {
            c.textContent = formatEntier.format(Math.round(valeurs[i]));
          }
        });
        if (equation) {
          const somme = valeurs.reduce((a, v) => a + v, 0);
          equation.textContent = `${valeurs.map((v) => formatEntier.format(Math.round(v))).join(" + ")} = ${formatEntier.format(Math.round(somme))}`;
        }
      };

      const remplissages = seaux.map(remplissage).filter((e): e is Element => e !== null);
      const pilules = LEDGER_SCENARIO.map((s) => pilule(s.step)).filter((e): e is SVGGraphicsElement => e !== null);
      const legendes = LEDGER_SCENARIO.map((s) => legende(s.step)).filter((e): e is Element => e !== null);

      return matchMotion(({ reduced, duration }) => {
        gsap.set(remplissages, { transformOrigin: "50% 100%" });
        gsap.set(pilules, { autoAlpha: 0 });
        gsap.set(legendes, { autoAlpha: 0 });
        if (total) {
          gsap.set(total, { transformOrigin: "0% 50%" });
        }

        if (reduced) {
          seaux.forEach((b, i) => {
            const r = remplissage(b);
            if (r) {
              gsap.set(r, { scaleY: final[i] / LEDGER_TOTAL });
            }
          });
          if (total) {
            gsap.set(total, { scaleX: 1 });
          }
          const derniere = legende(LEDGER_SCENARIO[LEDGER_SCENARIO.length - 1].step);
          if (derniere) {
            gsap.set(derniere, { autoAlpha: 1 });
          }
          ecrire(final);
          timeline.current = null;
          return;
        }

        gsap.set(remplissages, { scaleY: 0 });
        if (total) {
          gsap.set(total, { scaleX: 0 });
        }
        ecrire([0, 0, 0, 0]);

        const proxy = { a: 0, r: 0, c: 0, e: 0 };
        const tl = gsap.timeline({ defaults: { duration: duration(0.8) }, delay: duration(delay) });
        LEDGER_SCENARIO.forEach((etape, i) => {
          const [a, r, c, e] = soldes[i];
          const p = pilule(etape.step);
          const l = legende(etape.step);
          const precedente = i > 0 ? legende(LEDGER_SCENARIO[i - 1].step) : null;
          tl.addLabel(etape.step, i === 0 ? 0 : "+=0.5");
          if (precedente) {
            tl.to(precedente, { autoAlpha: 0, duration: duration(0.25) }, etape.step);
          }
          if (l) {
            tl.fromTo(l, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: duration(0.35) }, etape.step);
          }
          if (p) {
            const d = p.dataset;
            tl.fromTo(
              p,
              { autoAlpha: 0, x: Number(d.x0 ?? 0), y: Number(d.y0 ?? 0) },
              { autoAlpha: 1, x: Number(d.x1 ?? 0), y: Number(d.y1 ?? 0), ease: "power2.inOut" },
              etape.step,
            );
            tl.to(p, { autoAlpha: 0, duration: duration(0.25) }, ">-0.1");
          }
          // Le seau de départ baisse et celui d'arrivée monte au même instant :
          // c'est cette simultanéité qui montre qu'aucun token n'a disparu.
          const position = p ? "<-0.25" : etape.step;
          if (etape.from !== null) {
            const rf = remplissage(etape.from);
            if (rf) {
              tl.to(rf, { scaleY: soldes[i][seaux.indexOf(etape.from)] / LEDGER_TOTAL, duration: duration(0.6) }, position);
            }
          }
          const rt = remplissage(etape.to);
          if (rt) {
            tl.to(rt, { scaleY: soldes[i][seaux.indexOf(etape.to)] / LEDGER_TOTAL, duration: duration(0.6) }, etape.from !== null && remplissage(etape.from) ? "<" : position);
          }
          if (etape.from === null && total) {
            tl.to(total, { scaleX: 1, duration: duration(0.6) }, "<");
          }
          tl.to(proxy, { a, r, c, e, duration: duration(0.6), onUpdate: () => ecrire([proxy.a, proxy.r, proxy.c, proxy.e]) }, "<");
        });
        tl.call(() => ecrire(final));
        timeline.current = tl;
        return () => {
          tl.kill();
          timeline.current = null;
        };
      });
    },
    { scope: conteneur, dependencies: [delay] },
  );

  const replay = useCallback(() => {
    // `contextSafe` rattache le redémarrage au contexte du composant, comme
    // la secousse de refus : sans lui, un rejeu lancé depuis le bouton
    // survivrait au démontage.
    contextSafe(() => {
      timeline.current?.restart(true);
    })();
  }, [contextSafe]);

  return { ref: conteneur, replay };
}
